import { DEBUG } from '@src/DEBUG'; 
DEBUG.open ( '/app/commons/map.service.ts' );

import {Injectable} from '@angular/core';
import {
  Annotation,
  Client, Dataset,
  Folder,
  FourdCesiumHelper,
  FourDService,
  Job,
  Project,
  Transform,
  TransformGroup
} from '4d-mapper';
import {forkJoin, Observable, of, Subject} from 'rxjs';
import {concatMap, debounceTime, flatMap, map, take, tap} from 'rxjs/operators';

import {SearchQueryNode} from 'vasat'
import {APITypes} from './global';
import {Constants} from './Constants';
import {AclService} from './acl/acl.service';
import {HttpClient} from '@angular/common/http';
import { concat } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { WMSTransformRenderer } from '../v2/commons/cesium/cesium-renderer-wms';
import { XsApplicationEventsService } from '../xs/services/events/application-events.service';

import { XsEventListenerInterface } from '../xs/interfaces/event-listener.interface';
import { XsIdentityInterface } from '../xs/interfaces/identify.interface';
import { XsEventify } from '../xs/services/events/eventify.class';
import { AnnotationRenderer } from '../v2/commons/cesium/annotation-renderer';
import { Public3DRenderer } from '../v2/commons/cesium/public-3d-renderer';
import { ArcGisTransformRenderer } from '../v2/commons/cesium/cesium-renderer-arcgis';
import { WFSTransformRenderer } from '../v2/commons/cesium/cesium-renderer-wfs';
import { WMTSTransformRenderer } from '../v2/commons/cesium/cesium-renderer-wtms';
import { XsAwait } from '../xs/controllers/await.class';
import { AppService } from './app.service';
import { MapDataService } from '../v2/commons/map-data.service';


const Cesium = (<any>window).Cesium;

@Injectable({
  providedIn: 'root'
})
export class MapService implements XsIdentityInterface, XsEventListenerInterface {

  __ID      = null;
  __EVENTS  = null;

  debug:boolean = Constants.debug_off;
  debugPrimary:string = Constants._orange;
  debugSecondary:string = Constants._gray;

  debugData:any = null;

  canWrite: boolean
  // active project and scene
  currentProject: Project
  scene: TransformGroup

  // active photos, transforms and shapes
  sceneTransforms: Transform[] = []
  scenePhotos: Transform[] = []
  sceneShapes: any[] = []

  // project wide annotations
  annotations: Annotation[] = []

  // placeholder for draft annotation
  newAnnotation: Annotation
  newAnnotationReady: Subject<any> = new Subject<any>();

  // all scenes for project
  scenes: TransformGroup[] = []

  // cesium references
  helper: FourdCesiumHelper = null;
  viewer: any = null;

  isD360: any = null;
  isAgronomeye: any = null;

  // shares
  shareClients: Client[] = [];



  public static TOGGLE: string = 'toggle_';
  public static ALPHA: string = 'alpha_';
  public static VIEW: string = 'view_';

  public static FEET = 'Feet';
  public static METER = 'Meter';
  

  // observables for the various update events

  // What is sceneWChange change as opposed to the sceneChange? (@haresh ?)

  private _projectLoadingObs: Observable<Project> = null;
  private _sceneLoadingObs: Observable<TransformGroup> = null;

  // in loadScene() at the beginning
  private _sceneWChangeSubj = new Subject<Number>();
  private _sceneWChangeObj = this._sceneWChangeSubj.asObservable();

  // updateScene(), loadScene() towards the end, onSceneChange()
  private _sceneChangeSubj = new Subject<TransformGroup>();
  private _sceneChangeObj = this._sceneChangeSubj.asObservable();

  // loadCesium()
  private _cesiumReadySubj = new Subject<any>()
  private _cesiumReadyObj = this._cesiumReadySubj.asObservable()

  // refreshPhotos() start
  private _photoWChangeSubj = new Subject<Transform[]>()
  private _photoWChangeObs = this._photoWChangeSubj.asObservable()

  // refreshPhotos() end
  private _photoChangeSubj = new Subject<Transform[]>()
  private _photoChangeObs = this._photoChangeSubj.asObservable()

  /*
    Why is there an _annotChange* AND an _annot1Change*?
  */

  private _annotChangeSubj = new Subject<Annotation[]>()
  private _annotChangeObs = this._annotChangeSubj.asObservable()

  private _annot1ChangeSubj = new Subject<Annotation>()
  private _annot1ChangeObs = this._annot1ChangeSubj.asObservable()

  private _cameraDebouncerSub = new Subject<any>()
  private _cameraDebouncer = this._cameraDebouncerSub.asObservable().pipe(debounceTime(3000))

  private _projSubj = new Subject<Project>()
  private _projObs = this._projSubj.asObservable()


  projectSettingEPSGCodeChanged = new Subject<any>();
  projectSettingMeasurementUnitChanged = new Subject<any>();
  isCameraClicked: boolean;
  onSingleImageForAttachementChange = new Subject<Transform>();
  inTerrainCompare: boolean;
  terranCompareStarted: Subject<any> = new Subject<any>();
  private terranCompareStartedObs = this.terranCompareStarted.asObservable()
  terrainColorized: boolean;
  showHideAllPhotosSub: Subject<any> = new Subject<any>();
  showPhotos: Array<boolean> = [];
  resetLastLocation: boolean = false;
  localPhotosReadySub: Subject<any> = new Subject<any>();
  moveActive = false;
  slider: HTMLElement | null;
  private currentSplitIndex: number = 0;
  clipedEntity: any;
  private sideTransform: HTMLElement | null;
  leftSideTransform: string;
  rightSideTransform: string;
  flyToFirstTransform: boolean | undefined;
  datasetUploadingOrTilling: Subject<any> = new Subject<any>();




  // main list of incoming data files (uploading, waiting for action, processing, errors, etc.)
  datasetForLayers: Dataset[] = [];

  // incomingData:incomingDataManager = null;

  // permissions: AclService = null;

  hasLoadedAnnotations:boolean = false;

  // location marker (for a user's location)
  locationShape:any = null;

  // temporary for debugging
  debug_entities:any = [];

  // coordinates for users location
  // locationCartographic:any = new Cesium.Cartographic(0,0,20);

  // geolocation is the user's position, camera view, orientation and related info
  geolocation:any = {
    longitude: 0,
    latitude: 0,
    height:5,
    sampledHeight: 0,
    cameraHeight: 0
  };

  cameralocation:any = {
    longitude: 0,
    latitude: 0,
    height:5,
    sampledHeight: 0,
    cameraHeight: 0,
    dateClient:null,
    dateCamera:null,
    timeZone:null,
    timeLabel:null,
    simulatedTimeLabel:null
  };


  private INVALID_PROJECT = 'Invalid project'

  constructor(
    public fourD: FourDService, public permissions: AclService, public translate: TranslateService, private httpClient: HttpClient, 
    public applicationEvents: XsApplicationEventsService, public v2Maps:MapDataService
  ) {

    DEBUG.log ( '/app/commons/map.service.ts' );
    
    this.__ID = XsIdentityInterface.__init(this);
    this.__EVENTS = XsEventListenerInterface.__init(this);

    // refactor of datasetForLayers --- moved into incomingData.service.ts
    // this.incomingData = new incomingDataManager( this.fourD );


    translate.get('GENERIC.INVALID_PROJECT').subscribe(s => this.INVALID_PROJECT = s);

    // these new events are rewiring these old events for new ways of doing it throughout the application
    // --------------------------------------------------------------------------------------------------

    // register against the generic 'sceneChange' event, and inject our Subjects for Start and End
    applicationEvents.register ( 'mapServiceSceneChangeStart', this._sceneWChangeSubj, XsEventify.REGISTER_ONLY_ONE, this.__ID );
    applicationEvents.register ( 'mapServiceSceneChangeEnd', this._sceneChangeSubj, XsEventify.REGISTER_ONLY_ONE, this.__ID );

    // register against the generic 'photosChange' event, and inject our Subjects for Start and End
    applicationEvents.register ( 'mapServicePhotosChangeStart', this._photoWChangeSubj, XsEventify.REGISTER_ONLY_ONE, this.__ID );
    applicationEvents.register ( 'mapServicePhotosChangeEnd', this._photoChangeSubj, XsEventify.REGISTER_ONLY_ONE, this.__ID );

    // register against the generic 'annotationsChange' event, and inject our Subjects for Start and End
    applicationEvents.register ( 'mapServiceAnnotationsChangeStart', this._annotChangeSubj, XsEventify.REGISTER_ONLY_ONE, this.__ID );

    // register new events for transforms, and inject our Subjects for Start and End
    applicationEvents.register ( 'mapServiceTransformsChangeStart', null, XsEventify.REGISTER_ONLY_ONE, this.__ID );
    applicationEvents.register ( 'mapServiceTransformsChangeEnd', null, XsEventify.REGISTER_ONLY_ONE, this.__ID );







    // listen to the debounced camera updates (@alex sure, but what does that mean?)
    this._cameraDebouncer.subscribe(movement => {
      if (this.debug) { console.log('%c map.service %c cameraDebouncer()', this.debugPrimary, this.debugSecondary, movement); }
      forkJoin([
        this.v2Maps.onProjectReady().pipe(concatMap(_ => 
        this.v2Maps.annotations.state.pipe(take(1),tap(p => {
          if (this.debug) { console.log('map.service :: cameraDebouncer() READY', movement); }
          this.annotations = p;
          this.checkAndClipPlanesFromAnnotation();
          var v = p.slice()
          if (this.newAnnotation)
            v.push(this.newAnnotation)
          this._annotChangeSubj.next(v)
        })))),
        this.refreshPhotos(movement ? movement.endPosition : undefined)
      ]).subscribe()
    })

  }

  // listen out for when the mapViewer / CesiumMap is ready (ref; XsEventListenerInterface)
  ___mapViewer (obj:any = null ) {

    if ( this.debug ) { console.log ( '%c mapService %c ___mapViewer, caught them ...', this.debugPrimary, this.debugSecondary, obj ); }
    
    this.viewer = obj.viewer;
    this.helper = obj.helper;

    this._cesiumReadySubj.next()

  }


  onReady() {

    if (this.currentProject) {
      if (this.debug) { console.log('%c map.service %c onReady()', this.debugPrimary, this.debugSecondary, this.currentProject); }
      return of(this.currentProject)
    }
    if (this.debug) { console.log('%c map.service %c onReady() _projObs', this.debugPrimary, this.debugSecondary, this._projObs); }
    return this._projObs;

  }

  setNewAnnotation(a: Annotation) {
    this.newAnnotation = a
    var items = this.annotations.slice()

    if (a) {
      items.push(a)
      this._annotChangeSubj.next(items)
    }
    else {
      this._annotChangeSubj.next(items)
    }
  }

  updateAnnotation(a: Annotation, dontSave: boolean = false) {
    // just fire event
    if (dontSave) {
      this._annot1ChangeSubj.next(a)
    } else {
      a['saved'] = true;
      a['entity_config'] = null;
      return a.saveObservable().pipe(tap(_ => {
        this.newAnnotation = undefined;
        if (!this.annotations.find(aa => aa.id == a.id)) {
          this.annotations.push(a)
          this.v2Maps.annotations.add(a)
          this._annotChangeSubj.next(this.annotations.slice())
        }
        else {
          this._annot1ChangeSubj.next(a)
        }
      }))
    }
  }

  refreshAnnotations() {
    if (this.debug) { console.log('%c map.service %c refreshAnnotations()', this.debugPrimary, this.debugSecondary); }
    return this.v2Maps.onProjectReady().pipe(concatMap(_ => 
      this.v2Maps.annotations.state.pipe(take(1),tap(p => {
        if (this.debug) { console.log('%c map.service %c refreshAnnotations() READY', this.debugPrimary, this.debugSecondary ); }
        this.hasLoadedAnnotations = true;
        this.annotations = p;
        this.checkAndClipPlanesFromAnnotation();
        this._annotChangeSubj.next(p)
      })))
    )
  }

  /*
  setCameraHeight( height:number ) {
    if ( this.viewer && this.viewer.scene && this.viewer.scene.camera && this.viewer.scene.camera.position ) {
      let newcart = this.coordHelper.updateCesiumCartesianHeight(this.viewer.scene.camera.position, Math.round(height) );
      // console.log('@@@', height, this.viewer.scene.camera.position, newcart );
      this.viewer.scene.camera.position = newcart;
    }
  }
*/

  // hack; it's tricky to get a fourD.V into an Annotation, so just a quick helper to create a new one
  getNewTransform() {
    return new Transform(this.fourD.V);
  }

  /*
  refactor; disabling this bananas injection of the mapComponent just to pull out the coordinates. Just friggin 
            call RefreshPhotos with coordinates you may or may not have got, like all good children would do!

  refreshPhotosFromCurrentCord(mapComp: MapComponent) {
    if ( ! mapComp ) {
      return;
    }
    return this.refreshPhotos(mapComp.currentCoord ? mapComp.currentCoord.position : null, mapComp.vertical_offset)
  }
*/
  refreshPhotos(position = null, applyVerticalOffset: number = 0) {

    this._photoWChangeSubj.next([]);

    let query = this.scene.photos;

    if (position) {

      if ( this.debug ) { console.log ( '%c map.service %c refreshPhotos() 1. POSITION', this.debugPrimary, this.debugSecondary, position); }

      if ( position.latitude && ! Number.isNaN ( position.latitude ) ) {

        let q = position.latitude + ';' + position.longitude + ';'
        if (this.debug) { console.log('%c map.service %c refreshPhotos(pos,query,verticalOffset) 2. long,lat', this.debugPrimary, this.debugSecondary, position, q ); }

        if ( position.height && ! Number.isNaN ( position.height ) ) { 
          q = q + ( position.height - applyVerticalOffset ) + ';';
        }
        query = query.equals('camera', q);

      } else if ( position.x && ! Number.isNaN ( position.x ) ) {

        var carto = Cesium.Cartographic.fromCartesian(position);
        let q = carto.latitude + ';' + carto.longitude + ';';
        if (this.debug) { console.log('%c map.service %c refreshPhotos(pos,query,verticalOffset) 3. x,y,z', this.debugPrimary, this.debugSecondary, position, carto, q ); }

        if ( carto.height && ! Number.isNaN ( carto.height ) ) { 
          q = q + ( carto.height - applyVerticalOffset ) + ';';
        }
        query = query.equals('camera', q);
        
      } else {
        if ( this.debug ) { console.log ( '%c map.service %c refreshPhotos() 4. incoming position has non-numbers', this.debugPrimary, this.debugSecondary, position); }
      }
      
    }

    if (this.debug) { console.log('%c map.service %c refreshPhotos(pos,query,verticalOffset) 5.', this.debugPrimary, this.debugSecondary, position, query, applyVerticalOffset ); }

    return query.limit(1000003).queryObservable().pipe(tap(p => {
      this.scenePhotos = position ? p.items.sort((a, b) => {
        if (a['selected_position'] && a['selected_position'].x > 0 && (!b['selected_position'] || b['selected_position'].x == -1)) {
          return -10000000000
        }
        else if (b['selected_position'] && b['selected_position'].x > 0 && (!a['selected_position'] || a['selected_position'].x == -1)) {
          return 1000000000
        }
        else if (a['distance'] && b['distance'])
          return a['distance'] - b['distance']
        else
          return 0
      }) : p.items

      this._photoChangeSubj.next(p.items.slice())
    }))
  }

  /**
   * Update annotations using search
   * @param cesiumCamera
   */
  searchAnnots(complexQuery?: SearchQueryNode) {

    if ( ! this.currentProject || ! this.currentProject.id ) {
      return of(null);
    }

    var projectQ: SearchQueryNode = {
      op: '=',
      col: 'project',
      val: this.currentProject.id
    }

    if ( this.debug ) { console.log ( '%c MapService %c searchAnnots()', this.debugPrimary, this.debugSecondary, complexQuery ); }

    return this.fourD.searchFor(Annotation).complexQuery(
      complexQuery ?
        {
          op: 'and',
          items: [projectQ, complexQuery]
        } :
        projectQ
    ).limit(1000004).queryObservable().pipe(tap(p => {
      this.v2Maps.annotations.values = p.items
      this.annotations = p.items
      this.checkAndClipPlanesFromAnnotation();
      this._annotChangeSubj.next(p.items.slice())
    }))


  }

  /**
   *
   * @param a Delete an annotation from server
   */
  deleteAnnotation(a: Annotation) {
    if (a['clipPlanes'] !== undefined) {
      a['points'].forEach(point => {
        this.clipPlanes(point, false);
      })
    }
    a['saved'] = true;
    return a.destroyObservable().pipe(tap(_ => {
      this.newAnnotation = undefined;
      
      this.annotations = this.annotations.filter(aa => aa.id != a.id)
      this.v2Maps.annotations.values = this.annotations
      this._annotChangeSubj.next(this.annotations.slice())
    }))
  }

  /**
   *
   * @param a Remove all photos from active Scene
   */
  removeActiveScenePhotos() {
    return this.scene.removeAllImages().pipe(tap(_ => {
      this.scenePhotos = [];
      this.scene._photo_count = 0;
      delete this.localPhotos[this.scene.id];
      this._photoChangeSubj.next([])
    }))
  }

  /**
   * Update the position of the cesium camrea
   * @param cesiumCamera
   */
  updateCameraPosition(cesiumCamera: any, movment) {
    return this._cameraDebouncerSub.next(movment)
  }

  /**
   * Remove a transform from a scene
   * @param t
   */
  removeFromCurrentScene(t: Transform) {
    if (t['clipGlobe'] != undefined) {
      this.clipGlobe(false, t);
    }
    return this.scene.children.removeObservable(t).pipe(
      flatMap(_ => {
        delete this.scene['layerProps'][MapService.TOGGLE + t.id];
        delete this.scene['layerProps'][MapService.ALPHA + t.id];
        delete this.scene['layerProps'][MapService.VIEW + t.id];
        this.sceneTransforms = this.sceneTransforms.filter(s => s.id != t.id);
        this.saveSceneTransformsOrders();
        this.checkAndClipPlanesFromTransforms();
        this.scenePhotos = this.scenePhotos.filter(s => s.id != t.id)
        if (this.scene._cachedTransforms) {
          this.scene._cachedTransforms = this.scene._cachedTransforms.filter(tt => tt.id != t.id)
        }
        return this.scene.saveObservable()
      })
    )
  }

  /**
   * Remove a scene
   * @param tg
   */
  deleteScene(tg: TransformGroup) {
    if (this.currentProject['lastActiveScene'] == tg.id) {
      delete this.currentProject['lastActiveScene'];
      this.currentProject.saveObservable().subscribe();
    }
    return tg.destroyObservable().pipe(flatMap(_ => {

      this.scenes = this.scenes.filter(s => s.id != tg.id)
      if (this.scene && this.scene.id == tg.id) {
        if (this.scenes.length > 0) {
          this.loadScene(this.scenes[0].id).subscribe()
          return of('')
        }
        else {
          this.scene = null;
          this.sceneTransforms = null;
          this.checkAndClipPlanesFromTransforms();
          return of('')
        }
      }
    }))
  }


  /**
   * Update the scene without reloading allo of the scens meta
   * @param tg
   */
  updateScene(tg: TransformGroup) {
    this.scenes = this.scenes.filter(s => s.id != tg.id)
    this.scenes.push(tg)
    this.scene = tg
    // clear the list of layers
    this.sceneTransforms = [];
    this._sceneChangeSubj.next(tg)

    if (this.debug) { console.log('%c map.service %c updateScene()', this.debugPrimary, this.debugSecondary, tg, this.scenes ); }
    
  }

  layerPropsDefaults(t:any) {

    const check = [
      { key: MapService.TOGGLE + t.id, default: true },
      { key: MapService.ALPHA + t.id, default: 1 },
      { key: MapService.VIEW + t.id, default: true },
      { key: '_'+MapService.ALPHA + t.id, default: false }
    ];

    check.forEach ( item => {
      if ( !this.scene['layerProps'].hasOwnProperty(item.key) || this.scene['layerProps'][item.key] == undefined ) {
        this.scene['layerProps'][item.key] = item.default;
      }
    });

    // this.scene.saveObservable().subscribe();
  }

  /**
   * Update the scene by loading all of the scenes meta
   * @param tgId
   * @mustReloadScene if we want to forcefully reload scene in case of any job result available or any transforms added
   */
  loadScene(tgId: Number, mustReloadScene: boolean = true): Observable<TransformGroup> {

    const xtrue:boolean = false;
    
    var flyThere = null;
    var found = this.scenes.find(s => s.id == tgId);
    if (!found && this.scenes.length > 0) {
      found = this.scenes[0];
    }

    if (xtrue || this.debug) { console.log('%c map.service %c loadScene('+tgId+','+mustReloadScene+')', this.debugPrimary, this.debugSecondary, tgId, found, this.scenes ); }

    if (found && this.scene && this.scene == found && !mustReloadScene){
      if (xtrue || this.debug) { console.log('%c map.service %c loadScene() return', this.debugPrimary, this.debugSecondary, tgId, this.scene); }
      return of(this.scene)
    }

    else if (this._sceneLoadingObs) {
      if (xtrue || this.debug) { console.log('%c map.service %c loadScene() return sceneLoadingObs', this.debugPrimary, this.debugSecondary, tgId); }
      return this._sceneLoadingObs
    }

    // found a scene?
    else if (found) {

      if (xtrue || this.debug) { console.log('%c map.service %c loadScene() found', this.debugPrimary, this.debugSecondary, tgId); }

      this._sceneWChangeSubj.next(tgId)
      this.scene = found
      if (!found['layerProps'])
        found['layerProps'] = {}
      this.scenePhotos = [];
      this._sceneLoadingObs = found.children.limit(1000005).queryObservable().pipe(map(s => {


        if (xtrue || this.debug) { console.log('%c map.service %c loadScene() LOADING SCENE', this.debugPrimary, this.debugSecondary, tgId, s, this._sceneLoadingObs ); }

        this.applicationEvents.triggerStart('mapServiceTransformsChange');

        s.items.forEach(t => {

          // hack; reset this showOptions for all layers
          t['showOptions'] = false;

          this.layerPropsDefaults(t);

          // are any of these settings worth exposing in the UI? (@alex)
          if ((t['ds_type_uid'] == 'laslaz' || t['ds_type_uid'] == 'laslaz-tiles') && !t.pointCloudShading) {
            t.pointCloudShading = {
              'geometricErrorScale': 1,
              'attenuation': true,
              'maximumAttenuation': 8,
              'baseResolution': 0,
              'eyeDomeLighting': false,
              'eyeDomeLightingStrength': 1,
              'eyeDomeLightingRadius': 1
            }
            // t.saveObservable().subscribe();
          }

        });

        this.sceneTransforms = s.items;

        this.applicationEvents.triggerEnd('mapServiceTransformsChange', s.items );

        this.refreshPhotos().subscribe();
        
        this.reorderSavedSceneTranformOrders();
        this.checkAndClipPlanesFromTransforms();

        // found any to fly to?
        // if (flyThere) {
          // then fly!
          // if (this.debug) { console.log('%c map.service %c loadScene() flyTo', this.debugPrimary, this.debugSecondary, flyThere); }
          // this.helper.sendCameraTo(flyThere);
        // }
        this._sceneLoadingObs = null
        if (xtrue || this.debug) { console.log('%c map.service %c loadScene() trigger sceneChangeSubj', this.debugPrimary, this.debugSecondary, found); }
        this._sceneChangeSubj.next(found);
        if (xtrue || this.debug) { console.log('%c map.service %c loadScene() return found scene', this.debugPrimary, this.debugSecondary, found); }
        return found
      }));
      if (xtrue || this.debug) { console.log('%c map.service %c loadScene() return sceneLoadingObs', this.debugPrimary, this.debugSecondary, this._sceneLoadingObs); }
      return this._sceneLoadingObs
    } else {
      Constants.hideHangOn();
    }
    if (xtrue || this.debug) { console.log('%c map.service %c loadScene() return sceneChangeObj', this.debugPrimary, this.debugSecondary, this._sceneChangeObj); }
    return this._sceneChangeObj;
  }

  /**
   * Updaing the current project using the project ID
   */
  loadProject ( pid:Number, sceneId:number = null ): Observable<Project> {

    if ( this.debug ) { console.log('%c mapService %c loadProject()', this.debugPrimary, this.debugSecondary, pid, sceneId); }

    if (!this.fourD.projects) {
      // TODO you shouldnt have any automatic logic for chaning account here
      if ( this.debug ) { console.log('%c map.Service %c loadProject [no projects, piping]', this.debugPrimary, this.debugSecondary); }
      return this.fourD.onReady().pipe(
        flatMap(p => this.fourD.setActiveTeam(p[0].uid)),
        flatMap(_ => this.loadProject(pid, sceneId))
      );
    }
    var p = this.fourD.projects.find(p => p.id == pid);

    if ( this.debug ) { console.log ( '%c mapService %c loadProject pid, sceneId, found project', this.debugPrimary, this.debugSecondary, pid, sceneId, p ); }

    if (!p) {
      return Observable.throw({message: this.INVALID_PROJECT})
    }

    // if there is a current project, but it's different frome the newly loaded one
    if (this.currentProject && p.id != this.currentProject.id) {
      this.currentProject = null
    }

    // TODO wrong
    // if (this.currentProject == p && !sceneId || (sceneId && this.scene && this.scene.id === sceneId)) {
    //  this.refreshAnnotations().subscribe();
    // return of(p)
    // }

    //change last used date
    // p.saveObservable().subscribe(value => {
    //   value.fetchObservable().subscribe();
    // });

    if (this.currentProject) {
      if (this.debug) { console.log('%c map.service %c loadProject() RETURN this.currentProject', this.debugPrimary, this.debugSecondary ); }
      return of(this.currentProject)
    }

    if (!this._projectLoadingObs) {

      if (this.debug) { console.log('%c map.service %c loadProject() CREATE this._projectLoadingObs', this.debugPrimary, this.debugSecondary ); }

      this.currentProject = p;

      this._projectLoadingObs = forkJoin([
        p.shareClients.limit(1000006).queryObservable().pipe(
          tap(s => this.shareClients = s.items)
        ),
        p.groupList().pipe(
          tap(tgs => this.scenes = tgs)
        )
      ]).pipe(
        flatMap(_ => {
          if (sceneId) {
            if (this.debug) { console.log('%c map.service %c loadProject() map LoadScene(id)', this.debugPrimary, this.debugSecondary, p); }
            return this.loadScene(sceneId);
          } else if (p['lastActiveScene']) {
            if (this.debug) { console.log('%c map.service %c loadProject() map LoadScene(lastActiveScene)', this.debugPrimary, this.debugSecondary, p); }
            return this.loadScene(p['lastActiveScene']);
          } else {
            if (this.debug) { console.log('%c map.service %c loadProject() map LoadScene(n) PICK FIRST', this.debugPrimary, this.debugSecondary, p, this.scenes.length ? this.scenes[0].id : -1); }
            return this.loadScene(this.scenes.length ? this.scenes[0].id : -1)
          }
        }),
        map(_ => {

          // this.currentProject = p;

          // console.log ( 'mapService @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@' );

          this.isAgronomeye = this.fourD.apis.find(a => a.processorImpl == APITypes.Agronomeye.IMPL && a['project'] && a['project'].id == this.currentProject.id)

          this.canWrite = this.fourD.activeTeam.canWrite // || this.currentProject['isMember']
          this._projectLoadingObs = null

          this.isD360 = !!this.fourD.activeTeam.hasApiEnabled(APITypes.Datum360.NAME) && !!this.fourD.getApi(APITypes.Datum360.NAME);

          if (this.debug) { console.log('%c map.service %c loadProject() !! CREATED !! this._projectLoadingObs', this.debugPrimary, this.debugSecondary, p); }

          this._projSubj.next(p);

          return p;
        })
      );
    }

    if (this.debug) { console.log('%c map.service %c loadProject() RETURN this._projectLoadingObs', this.debugPrimary, this.debugSecondary ); }

    return this._projectLoadingObs;
  }

  deinit(){
    this.scenes = []
    this.currentProject = null
    this.sceneTransforms = []
    this.annotations = []
    this.scene = null;
  }
  /**
   * Generate a share link
   * @param email email to send invite to
   * @param pin the point to mark when share is loaded
   * @param camera position of camera
   */
  addShare(inp: any, camera?: any) {

    // inp = { email:'...', permissions:{...}, expire: false|number }

    let name = inp['name'] ? inp['name'] : '';
    let email = inp['email'] ? inp['email'] : '';
    let p = inp['roles'] ? inp['roles'] : [];
    let e = inp['expire'] ? inp['expire'] : false;

    let obj = {name: name, email: email, message:inp.message, camera: camera, activeSceneId: this.scene.id, permissions: p.map(p => p.id), expiration: e};

    // console.log('map-service : addShare', inp, '/share/' + this.currentProject.id, obj);

    return this.fourD.V.api('/share/' + this.currentProject.id, 'post', obj).pipe(flatMap(_ =>
      this.currentProject.shareClients.limit(1000007).queryObservable().pipe(tap(s => this.shareClients = s.items))
    ));
  }

  /**
   * remove a share client
   * @param c
   */
  removeShare(c: Client) {
    return c.destroyObservable().pipe(tap(s =>
      this.shareClients = this.shareClients.filter(cc => cc != c)
    ))
  }

  /**
   * Triggers when the active scene is changed
   */
  onSceneChange( source:string = 'unknown' ) {
    // if (true || this.debug) { console.log('%c @@@@@@@@@@@@@@@@@@@@@@@@ map.service :: %c onSceneChange() ['+ source +'] @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@', this.debugPrimary, this.debugSecondary, this.scene ); }

    /*
    if (this.scene) {
      setTimeout(() => {
        if (this.scene) {
          this._sceneChangeSubj.next(this.scene)
        } else {
          Constants.hideHangOn();
        }
      }, 200)
    } else {
      // if no scene then hide the hangon dialog
      Constants.hideHangOn();
    }
    */

    if(this.scene)
      return concat([this.scene],this._sceneChangeObj)
    else
      return this._sceneChangeObj

/*
    if (this.scene) {
      Constants.hideHangOn();
      this._sceneChangeSubj.next(this.scene);
    } else {
      return this._sceneChangeObj
    }
  */  
  }

  /**
   * Triggered when active scene will start changed (used for showing a loading spinner)
   */
  onSceneWillChange() {

    return this._sceneWChangeObj
  }

  /**
   * trigger when annotation list changes (due to search or camera position)
   */
  onAnnotationsChange() {
    return this._annotChangeObs
  }

  onTerranCompareStarted() {
    return this.terranCompareStartedObs;
  }

  onCesiumReady() {
    return this._cesiumReadyObj;
  }

  /**
   * trigger when a single annotation changes (due to meta data being updated)
   */
  onSingleAnnotationChange() {
    return this._annot1ChangeObs
  }

  /**
   * trigger when photos change (due to search or camera position)
   */
  onPhotosChange() {
    return this._photoChangeObs
  }

  /**
   * trigger when photos change (due to search or camera position)
   */
  onPhotosWillChange() {
    return this._photoWChangeObs
  }



  /**
   * Check if a user / profile has seen the given tip. If not, display it.
   */

  checkForTip(tip: string, identifier: string) {

    // tip is an identifier for looking up the tip in the language file (in the 'TIPS' section)
    // identifier is a DOM identifier the tooltip box will point to / talk about


  }


  /*   refactored into its own component


  splitTransforms() {
    if (this.slider && !this.slider.hidden) {
      return;
    }
    this.slider = document.getElementById('slider');
    this.sideTransform = document.getElementById('sideTransform');
    this.slider.hidden = false;
    this.sideTransform.hidden = false;

    if (this.viewer.imageryLayers._layers.length > 0) {
      this.currentSplitIndex = 0;
      let firstActiveTransform = undefined;
      let secondActiveTransform = undefined;
      for (let st of this.sceneTransforms) {
        if (this.scene['layerProps'][MapService.TOGGLE + st.id] && this.isValidForChangingOpacity(st) && !firstActiveTransform) {
          firstActiveTransform = st;
        } else if (this.scene['layerProps'][MapService.TOGGLE + st.id] && this.isValidForChangingOpacity(st) && firstActiveTransform) {
          secondActiveTransform = st;
          break;
        }

      }
      if (firstActiveTransform) {

        this.viewer.imageryLayers._layers.forEach(l => {
          if (l._imageryProvider._resource.url.indexOf(firstActiveTransform.id) !== -1) {
            return;
          }
          this.currentSplitIndex++;
        })
        if (this.currentSplitIndex < this.viewer.imageryLayers._layers.length) {
          this.leftSideTransform = firstActiveTransform.ds_name;
          if (secondActiveTransform) {
            this.rightSideTransform = secondActiveTransform.ds_name;
          } else {
            this.rightSideTransform = ' ';
          }
          this.viewer.imageryLayers._layers[this.currentSplitIndex].splitDirection = Cesium.ImagerySplitDirection.LEFT;
          this.viewer.scene.imagerySplitPosition = (this.slider.offsetLeft) / this.slider.parentElement.offsetWidth;
          
          var handler = new Cesium.ScreenSpaceEventHandler(this.slider);
          handler.setInputAction(() => {
            this.moveActive = true;
          }, Cesium.ScreenSpaceEventType.LEFT_DOWN);
          handler.setInputAction(() => {
            this.moveActive = true;
          }, Cesium.ScreenSpaceEventType.PINCH_START);
          handler.setInputAction(this.move.bind(this), Cesium.ScreenSpaceEventType.MOUSE_MOVE);
          handler.setInputAction(this.move.bind(this), Cesium.ScreenSpaceEventType.PINCH_MOVE);
          handler.setInputAction(() => {
            this.moveActive = false;
          }, Cesium.ScreenSpaceEventType.LEFT_UP);
          handler.setInputAction(() => {
            this.moveActive = false;
          }, Cesium.ScreenSpaceEventType.PINCH_END);
        } else {
          this.slider.hidden = true;
          this.sideTransform.hidden = true;
        }
      } else {
        this.slider.hidden = true;
        this.sideTransform.hidden = true;
      }
    }

  }


  move(movement) {
    if (!this.moveActive) {
      return;
    }

    var relativeOffset = movement.endPosition.x;
    var splitPosition = (this.slider.offsetLeft + relativeOffset) / this.slider.parentElement.offsetWidth;
    this.slider.style.left = 100.0 * splitPosition + '%';
    this.viewer.scene.imagerySplitPosition = splitPosition;
  }

  unsplitTransforms() {
    this.leftSideTransform = undefined;
    this.rightSideTransform = undefined;
    if (this.slider && !this.slider.hidden) {
      this.slider.hidden = true;
      this.sideTransform.hidden = true;
      if (this.currentSplitIndex !== 0) {
        if (this.currentSplitIndex < this.viewer.imageryLayers._layers.length) {
          this.viewer.imageryLayers._layers[this.currentSplitIndex].splitDirection = 0;
          this.viewer.scene.imagerySplitPosition = 0;
        }
        this.reorderTransform();
      }
    }
  }

  */

  /* OLDER items from Heresh, look to standardize */

  // Alex: I've killed this one, doesn't seem to be in use anywhere except photogrammetry, and there it just listens for it, but no one is setting it
  // loadedProject: Subject<Project> = new Subject<Project>();


  transformsFromFolder: Subject<Folder> = new Subject<Folder>();
  FOURDDMAPPER_REFERENCE: string = 'REFERENCE';
  FOURDDMAPPER_REFERENCE_TICK: string = 'REFERENCE_TICK';
  FOURDDMAPPER_META: string = 'META';
  DATA_FROM_FOLDER: string = 'DATA_FROM_FOLDER';
  REFERENCE_ANNOTATION = 'REFERENCE_ANNOTATION';
  loadImages: Subject<any> = new Subject<any>();
  selectedReference: { pos: string, referenceAnnotation: any, job: Job };
  JOB_EPSG_CODE = 'JOB_EPSG_CODE';
  public static DEFAULT_GROUP = 'DEFAULT';

  public localPhotos: any = [];

  JOB_EPSG_CODE_DESC = 'JOB_EPSG_CODE_DESC';
  JOB_REF_EPSG_CODE = 'JOB_REF_EPSG_CODE';
  JOB_REF_EPSG_CODE_DESC = 'JOB_REF_EPSG_CODE_DESC';

  // AAAAAAAAAAARGH! What is a Cesium map function doing here!?! TODO : For this type of stuff, we should do this in the map.component.ts controller

  changeBaseModelColor(incoming:any = null) {
    let color = this.scene ? ( this.scene['baseModelColor'] ? this.scene['baseModelColor'] : '#000' ) : '#444';
    if ( incoming ) {
      color = incoming;
    }
    
    this.viewer.scene.globe.baseColor = Cesium.Color.fromCssColorString(color);
    
    // console.log('map.service :: changeBaseModelColor()', color, this.viewer.scene.globe.baseColor, this.viewer.scene.backgroundColor );
  }
  showBaseModel(b: any) {
    // if (this.debug) {
    // }
    //if ( this.viewer && this.viewer.scene ) {

      // console.log('map.service :: showBaseModel()', b );
    let me = this;

    XsAwait.until( 'viewer.scene', this ).then ( _ => {

      me.viewer.scene.imageryLayers._layers[0].show = b;
      if (!b) {
        me.changeBaseModelColor();
        me.viewer.scene.backgroundColor = Cesium.Color.WHITE;
      } else {
        me.changeBaseModelColor('#fff');
        me.viewer.scene.backgroundColor = Cesium.Color.BLACK;
      }

    });

      //try {
        
      //} catch (e) {
      //  console.log(e.message);
      //}

    //} else {
      // console.log('map.service :: showBaseModel() ERROR', b );
    //}
  }

  showGlobeOnCesium(show: boolean) {
    if (this.debug) {
      console.log('map.service :: showGlobeOnCesium()');
    }
    if (this.viewer) {
      this.viewer.scene.globe.show = show;
      this.viewer.skyAtmosphere = show;
      this.viewer.scene.skyBox.show = show; //  = new Cesium.SkyBox({ show: show });
    }
  }

  clipPlanes(points: any, clip: boolean) {
    if (this.debug) {
      console.log('map.service :: clipPlanes()');
    }
    if (points && points.length < 3) {
      // this.openSnackBarError('You cannot clip less than 3 points.');
      return;
    }
    // Create clipping planes for polygon around area to be clipped.
    var pointsLength = points.length;
    var clippingPlanesEnabled = true;
    var edgeStylingEnabled = true;

    // Create center points for each clipping plane
    var clippingPlanes = [];
    for (var i = 0; i < pointsLength; ++i) {
      var nextIndex = (i + 1) % pointsLength;
      var midpoint = Cesium.Cartesian3.add(points[i]['coords'], points[nextIndex]['coords'], new Cesium.Cartesian3());
      midpoint = Cesium.Cartesian3.multiplyByScalar(midpoint, 0.5, midpoint);

      var up = Cesium.Cartesian3.normalize(midpoint, new Cesium.Cartesian3());
      var right = Cesium.Cartesian3.subtract(points[nextIndex]['coords'], midpoint, new Cesium.Cartesian3());
      right = Cesium.Cartesian3.normalize(right, right);

      var normal = Cesium.Cartesian3.cross(right, up, new Cesium.Cartesian3());
      normal = Cesium.Cartesian3.normalize(normal, normal);

      // Compute distance by pretending the plane is at the origin
      var originCenteredPlane = new Cesium.Plane(normal, 0.0);
      var distance = Cesium.Plane.getPointDistance(originCenteredPlane, midpoint);

      clippingPlanes.push(new Cesium.ClippingPlane(normal, distance));
    }
    this.viewer.scene.globe.clippingPlanes = new Cesium.ClippingPlaneCollection({
      planes: clippingPlanes,
      edgeWidth: edgeStylingEnabled ? 1.0 : 0.0,
      edgeColor: Cesium.Color.WHITE,
      enabled: clippingPlanesEnabled,
      unionClippingRegions: clip
    });
  }

  clipGlobe(event, transform: Transform) {
    if (this.debug) {
      console.log('map.service :: clipGlobe()');
    }
    if (event) {
      var centerPosition = [(transform.llng + transform.ulng) / 2, (transform.llat + transform.ulat) / 2];
      var position = Cesium.Cartesian3.fromDegrees(centerPosition[0], centerPosition[1], transform.height);
      var pos_ll = new Cesium.Cartesian3.fromRadians(transform.llng, transform.llat, 0);
      var pos_ur = new Cesium.Cartesian3.fromRadians(transform.ulng, transform.ulat, 0);
      var distance = Cesium.Cartesian3.distance(pos_ll, pos_ur);
      distance = distance - 100;
      if (!this.clipedEntity) {
        this.clipedEntity = this.viewer.entities.add({
          name: 'clipped globe',
          position: position,
        });
        var globe = this.viewer.scene.globe;
        var clippingPlanesEnabled = event;
        var edgeStylingEnabled = event;
        globe.clippingPlanes = new Cesium.ClippingPlaneCollection({
          modelMatrix: this.clipedEntity.computeModelMatrix(Cesium.JulianDate.now()),
          planes: [
            new Cesium.ClippingPlane(new Cesium.Cartesian3(1.0, 0.0, 0.0), distance),
            new Cesium.ClippingPlane(new Cesium.Cartesian3(-1.0, 0.0, 0.0), distance),
            new Cesium.ClippingPlane(new Cesium.Cartesian3(0.0, 1.0, 0.0), distance),
            new Cesium.ClippingPlane(new Cesium.Cartesian3(0.0, -1.0, 0.0), distance)
          ],
          edgeWidth: edgeStylingEnabled ? 1.0 : 0.0,
          edgeColor: Cesium.Color.BLACK,
          enabled: clippingPlanesEnabled
        });
        this.viewer.scene.globe.baseColor = Cesium.Color.BLACK;
        this.viewer.scene.backgroundColor = Cesium.Color.BLACK;
      }

    } else {
      if (this.clipedEntity) {
        this.viewer.entities.remove(this.clipedEntity);
        this.clipedEntity = undefined;
      }
      this.viewer.scene.globe.clippingPlanes = undefined;
    }
    this.viewer.scene.imageryLayers._layers[0].show = !event;
  }

  private checkAndClipPlanesFromAnnotation() {
    if (this.debug) {
      console.log('map.service :: checkAndClipPlanesFromAnnotation()');
    }
    this.annotations.forEach(a => {
      if (a['clipPlanes'] !== undefined) {
        a['points'].forEach(point => {
          this.clipPlanes(point, a['clipPlanes']);
        })
      }
    })
  }

  changeOpacity(t: Transform, _saveSubj?: any, event?: any) {

    if ( this.debug ) { console.log ( 'map.service :: changeOpacity()' ); }

    if (this.isValidForChangingOpacity(t)) {

      var obj = this.helper.getCesiumObjectFor(t);

      //if ( this.debug ) { console.log ( 'map.service :: changeOpacity() transform, obj', t, obj ); }

      if (!obj) return;

      var opacity = event ? event.value : this.scene['layerProps'][MapService.ALPHA + t.id];

      if (t['ds_type_uid'] === 'geo-json' || t['ds_type_uid'] === 'kml' || t['ds_type_uid'] === 'shape-zip' && obj._entityCollection) {

        //if ( this.debug ) { console.log ( 'map.service :: changeOpacity() :: entities.forEach' ); }

        obj._entityCollection.values.forEach(e => {

          if ( this.debug ) { console.log ( 'map.service :: changeOpacity() :: entity', e ); }

          if (e.polyline && e.polyline.material && e.polyline.material.color) {
            var color = new Cesium.Color(e.polyline.material.color._value.red, e.polyline.material.color._value.green, e.polyline.material.color._value.blue, opacity);
            e.polyline.material.color = color;
          } else if (e.polygon && e.polygon.material && e.polygon.material.color) {
            var color = new Cesium.Color(e.polygon.material.color._value.red, e.polygon.material.color._value.green, e.polygon.material.color._value.blue, opacity);
            e.polygon.material.color = color;

            if (opacity == 1) {
              e.polygon.outline = true;
            } else {
              e.polygon.outline = false;
            }
          }
        })
      }

      const c = 'rgba(255, 255, 255,' + Number(opacity) + ')';

      //if ( this.debug ) { console.log ( 'map.service :: changeOpacity() :: opacity', c, opacity, obj ); }

      // for objects that supports the alpha
      obj.alpha = opacity;

      // for objects that support tileStyles (uh, meaning, tiles ...)
      obj.style = new Cesium.Cesium3DTileStyle({
        color: t['colourStyle'] || c
      });

      if (_saveSubj)
        _saveSubj.next(this.viewer.scene);
    } else {

    }

  }

  reorderTransform() {
    // this.debug = true;
    if (this.debug) { console.log('%c map.service %c reorderTransform()', this.debugPrimary, this.debugSecondary, this.sceneTransforms, this.viewer); }
    // let final = [];

    // always have your terrain models at the bottom
    this.sceneTransforms = [ ...this.sceneTransforms.filter( t => t['isTerrain'] ), ...this.sceneTransforms.filter( t => !t['isTerrain'] ) ];

    if ( !this.currentProject['_coords'] ) {
      this.currentProject['_coords'] = {};
    }
    
    // if ( this.scene && !this.scene['cameraPosition'] ) {
    //   this.scene['cameraPosition'] = { x:this.viewer.camera.position.x, y:this.viewer.camera.position.y, z:this.viewer.camera.position.z };
    // }
    this.currentProject['_coords'][this.scene.id] = {
      default:  { x:this.viewer.camera.position.x, y:this.viewer.camera.position.y, z:this.viewer.camera.position.z }
    };
    
    for (let i = this.sceneTransforms.length - 1; i >= 0; i--) {
      let t = this.sceneTransforms[i];
      let cObj = this.helper.getCesiumObjectFor(t);
      this.currentProject['_coords'][this.scene.id][t.id] = this.getCoordsFromItem(t);

      let meta:any = {};
      if ( this.debug ) {
        meta = {
          idx: i,
          toggle: this.scene['layerProps'][MapService.TOGGLE + t.id],
          show: this.scene['layerProps'][MapService.VIEW + t.id],
          name: this.sceneTransforms[i].name,
          type: this.sceneTransforms[i].transformType.uid,
          transform: t,
          object: cObj
        };
      }

      // this.debug = true;
      if (cObj && 
        (this.sceneTransforms[i].transformType.uid === 'tms' || this.sceneTransforms[i].transformType.uid === 'wms-remote' || this.sceneTransforms[i].transformType.uid === 'arcgis-remote')
        ) {
        this.viewer.imageryLayers.raiseToTop(cObj);
        
        if (this.debug) { console.log('%c map.service %c reorderTransform('+i+') to top', this.debugPrimary, this.debugSecondary, meta ); }
      } else {
        if (this.debug) { console.log('%c map.service %c reorderTransform('+i+') to top (but not TMS)', this.debugPrimary, this.debugSecondary, meta ); }
      }
      // this.debug = false;
    }



    // console.log('@@@@', this.currentProject['_coords'][this.scene.id] );

  }

  getCoordsFromItem ( item: any ) {

    let coords = { latitude: null, longitude: null, height: null };

    if (  item['firstTransformCoords'] ) {
      coords = item['firstTransformCoords'];
    }

    if ( item['__cameras'] && item['__cameras'][0] ) {
      const carto  = Cesium.Ellipsoid.WGS84.cartesianToCartographic( item['__cameras'][0]['data']['position'] );
      if ( carto && carto['longitude'] ) {
        coords.longitude = Cesium.Math.toDegrees(carto.longitude);
        coords.latitude = Cesium.Math.toDegrees(carto.latitude);
        coords.height = carto.height;
      }
    }

    if ( item.props['cameraPosition'] && item.props['cameraPosition']['position'] ) {
      const carto  = Cesium.Ellipsoid.WGS84.cartesianToCartographic( item.props['cameraPosition']['position'] );
      if ( carto && carto['longitude'] ) {
        coords.longitude = Cesium.Math.toDegrees(carto.longitude);
        coords.latitude = Cesium.Math.toDegrees(carto.latitude);
      }
    }

    if ( item['cameraPosition'] ) {
      if ( item['cameraPosition']['longitude'] ) {
        coords = item['cameraPosition'];
      } else {
        const carto  = Cesium.Ellipsoid.WGS84.cartesianToCartographic( item['cameraPosition'] );
        if ( carto && carto['longitude'] ) {
          coords.longitude = Cesium.Math.toDegrees(carto.longitude);
          coords.latitude = Cesium.Math.toDegrees(carto.latitude);
        }
      }
    }

    if ( item.llng && item.llat ) {
      coords.longitude = item.llng;
      coords.latitude = item.llat;
      coords.height = item.lheight;
    }

    return coords;

  }

  flyTo(annotation) {
    if (this.debug) {
      console.log('map.service :: flyTo(annotation)', annotation);
    }
    if (this.helper.getCesiumObjectFor(annotation)) {
      this.viewer.flyTo(this.helper.getCesiumObjectFor(annotation).pin, {
        duration: 1,
        maximumHeight: this.viewer.camera.height,
        offset: new Cesium.HeadingPitchRange(this.viewer.camera.heading, this.viewer.camera.pitch, this.getCameraHeight())
      });
    }
  }

  flyToRefPin(refPin) {
    if (this.debug) {
      console.log('map.service :: flyToRefPin(refPin)', refPin);
    }
    this.viewer.flyTo(refPin, {
      duration: 1,
      maximumHeight: this.viewer.camera.height,
      offset: new Cesium.HeadingPitchRange(this.viewer.camera.heading, this.viewer.camera.pitch, this.getCameraHeight())
    });
  }

  getCameraHeight(): number {
    if (this.debug) {
      console.log('map.service :: getCameraHeight()');
    }
    var camera = this.viewer.scene.camera;
    var ellipsoid = this.viewer.scene.globe.ellipsoid;
    var camera_height = ellipsoid.cartesianToCartographic(camera.position).height;
    return camera_height;
  }


  isValidForChangingOpacity(t: Transform) {

    // less concise but clearer and extendable version

    if ( ! t ) {
      return false;
    }
    
    // no opacity for laslaz and terrains (note: the colorizer plugin enables opacity for some of these)
    if ( t && (  t['ds_type_uid'] == 'laslaz-tiles' || t['type_uid'] == 'terrain-tiles' ) ) {
      return false;
    }

    // hack!!! Any layer with 'Tower' in the name can't have opacity because we currently can't deal with older tilesets properly
    if ( t.name.indexOf('Tower') !== -1 ) {
      return false;
    }
    // console.log ( 'ss:', t['ds_type_uid'], t['type_uid'], t.name.indexOf('Tower') );
    return true;
  }

  private checkAndClipPlanesFromTransforms() {
    if (this.debug) {
      console.log('map.service :: checkAndClipPlanesFromTransforms()');
    }
    if (this.sceneTransforms && this.sceneTransforms.length > 0) {
      this.sceneTransforms.forEach(t => {
        if (t['clipGlobe'] != undefined) {
          this.clipedEntity = undefined;
          this.clipGlobe(t['clipGlobe'], t);
          return;
        }
      })
    }

  }

  // revert all the cesium functionality which was disabled due to fake location
  enableCesiumFunctionsForFakeLocation(enable: boolean) {
    this.viewer.scene.globe.show = enable;
    this.viewer.skyAtmosphere = enable;
    this.viewer.scene.skyBox = new Cesium.SkyBox({
      show: enable
    });
    this.viewer.scene.screenSpaceCameraController.enableRotate = enable;
    this.viewer.scene.screenSpaceCameraController.enableTranslate = enable;
    this.viewer.scene.screenSpaceCameraController.enableZoom = enable;
    this.viewer.scene.screenSpaceCameraController.enableTilt = enable;
    this.viewer.scene.screenSpaceCameraController.enableLook = enable;
  }

  saveSceneTransformsOrders() {
    if (this.debug) {
      console.log('%c map.service %c saveSceneTransformsOrders()', this.debugPrimary, this.debugSecondary );
    }

    if (this.scene) {
      if (this.sceneTransforms.length > 0) {
        this.scene['sceneTransformsOrder'] = this.sceneTransforms.map(st => st.id);
      } else {
        delete this.scene['sceneTransformsOrder'];
      }
      this.scene.saveObservable().subscribe();
    }
  }

  // reorder the scene transforms order
  private reorderSavedSceneTranformOrders() {
    var original = this.sceneTransforms
    if (this.debug) {
      console.log('%c map.service %c reorderSavedSceneTranformOrders()', this.debugPrimary, this.debugSecondary );
    }
    if (this.scene['sceneTransformsOrder'] && this.scene['sceneTransformsOrder'].length > 0) {
      let transformsCopy = [];
      // if saved sceneTransformsOrder array is not same as current sceneTransforms  means current sceneTransforms has been updated so push the recently added transforms in top of the order list
      if (this.sceneTransforms.length > this.scene['sceneTransformsOrder'].length) {
        this.sceneTransforms.forEach(st => {
          let index = this.scene['sceneTransformsOrder'].findIndex(id => st.id === id);
          if (index === -1) {
            transformsCopy.push(st);
          }
        });
      }
      this.scene['sceneTransformsOrder'].forEach(id => {
        let index = this.sceneTransforms.findIndex(st => st.id === id);
        if (index !== -1) {
          transformsCopy.push(this.sceneTransforms[index]);
        }
      });
      this.sceneTransforms = transformsCopy;
    }

    if(original.length != this.sceneTransforms.length){
      // SJ - Dunno how this happens but it does some times, not gonna look any harder, its sat night, im tried
      // revert to the original list
      this.sceneTransforms = original
    }
  }

  getTransformColorFile(transform: Transform) {
    return this.httpClient.get(transform.file('color.txt'), {responseType: 'text'});
  }

  getTransformHeightFile(transform: Transform) {
    return this.httpClient.get(transform.file('minmax.txt'), {responseType: 'text'});
  }


  set annotationValues(a:Annotation[]){
    this.annotations = a
    this._annotChangeSubj.next(a.slice())
  }
}
